import {useQuery} from '@github-ui/react-query'
import {NavList, Spinner} from '@primer/react'
import {
  DEFAULT_ITEMS_PER_PAGE,
  type DynamicSidePanelItem,
  DynamicSidePanelItems,
  useSidePanelItemsQuery,
  type DynamicMenuItemsProps,
} from './DynamicSidePanelItems'
import {PinnableDynamicSidePanelItems, type PinnableDynamicMenuItemsProps} from './PinnableDynamicSidePanelItems'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {TeamsBanner} from './TeamsBanner'

export const PIN_UNPIN_PATH = '/dashboard/favorites'
export const RICH_CONTENT_DEFAULT_PATH = '/_side-panels/global.json'
export const REPOS_SEARCH_PATH = '/_side-panel-items/global/repositories'
export const PINNED_ITEMS_SEARCH_PATH = '/_side-panel-items/global/pinned_items'
export const TEAMS_SEARCH_PATH = '/_side-panel-items/global/teams'

export interface DynamicSidePanelContentProps {
  richContentUrl?: string
  repositoriesSearchUrl?: string
  teamsSearchUrl?: string
  pinnedItemsSearchUrl?: string
  pinUrl?: string
  itemsPerPage?: number
}

export interface RichContentData {
  repositories?: DynamicSidePanelItem[]
  teams?: DynamicSidePanelItem[]
  pinned_items?: {
    repositories?: DynamicSidePanelItem[]
  }
  show_teams_banner?: boolean
}

export function useRichContent(props: DynamicSidePanelContentProps) {
  const queryUrl = props.richContentUrl ?? RICH_CONTENT_DEFAULT_PATH
  const itemsPerPage = props.itemsPerPage ?? DEFAULT_ITEMS_PER_PAGE

  const query = useQuery({
    queryKey: ['global-nav-side-panel', queryUrl, itemsPerPage],
    queryFn: async () => {
      if (!queryUrl) return null
      const response = await reactFetchJSON(`${queryUrl}?items_per_page=${itemsPerPage}`)
      if (!response.ok) throw new Error('Failed to fetch')
      return response.json()
    },
  })

  return query
}

export function DynamicSidePanelContent(props: DynamicSidePanelContentProps) {
  const {data, isLoading, error} = useRichContent(props)

  if (error || isLoading) {
    return (
      <NavList.Group className="d-flex flex-justify-center width-full my-3">
        <Spinner data-testid="dynamic-side-content-loader" size="small" />
      </NavList.Group>
    )
  }

  return (
    <>
      {data.repositories?.length > 0 && (
        <TopRepos initialItems={data.repositories} searchUrl={props.repositoriesSearchUrl ?? REPOS_SEARCH_PATH} />
      )}
      {data.teams?.length > 0 && (
        <TopTeams initialItems={data.teams} searchUrl={props.teamsSearchUrl ?? TEAMS_SEARCH_PATH} />
      )}
      {data.pinned_items && data.pinned_items.repositories && (
        <PinnedRepos
          initialItems={data.pinned_items.repositories}
          pinUrl={props.pinUrl ?? PIN_UNPIN_PATH}
          searchUrl={props.pinnedItemsSearchUrl ?? PINNED_ITEMS_SEARCH_PATH}
        />
      )}
      {data.show_teams_banner !== undefined && <TeamsBanner showTeamsBanner={data.show_teams_banner} />}
    </>
  )
}

export function TopTeams(props: DynamicMenuItemsProps) {
  const {fetchNextPage, hasNextPage, isLoading, data, error} = useSidePanelItemsQuery(props)
  const hideTeams = isFeatureEnabled('global_nav_react_teams_settings_page')

  if (error || hideTeams) {
    return null
  }

  return (
    <DynamicSidePanelItems
      title="Teams"
      items={data.pages}
      searchLabel="Search for teams"
      searchUrl={props.searchUrl}
      itemsPerPage={props.itemsPerPage}
      isLoading={isLoading}
      hasNextPage={hasNextPage}
      fetchNextPage={fetchNextPage}
      analytics={{
        subCategory: 'TEAMS',
        showMoreAction: 'SHOW_MORE_TEAMS',
        itemAction: 'TEAM_ITEM',
      }}
    />
  )
}

export function TopRepos(props: DynamicMenuItemsProps) {
  const {fetchNextPage, hasNextPage, isLoading, data, error} = useSidePanelItemsQuery(props)

  if (error) {
    return null
  }

  return (
    <DynamicSidePanelItems
      title="Top repositories"
      items={data.pages}
      searchLabel="Search for repositories"
      itemsPerPage={props.itemsPerPage}
      searchUrl={props.searchUrl}
      isLoading={isLoading}
      hasNextPage={hasNextPage}
      fetchNextPage={fetchNextPage}
      analytics={{
        subCategory: 'REPOSITORIES',
        showMoreAction: 'SHOW_MORE_REPOS',
        itemAction: 'REPOSITORY_ITEM',
      }}
    />
  )
}

export function PinnedRepos(props: PinnableDynamicMenuItemsProps) {
  if (!isFeatureEnabled('global_nav_react_sidebar_pinned_repos')) {
    return null
  }

  return (
    <PinnableDynamicSidePanelItems
      title="Favorites"
      items={props.initialItems}
      analytics={{
        subCategory: 'PINNED_ITEMS',
        itemAction: 'PINNED_REPOSITORY_ITEM',
      }}
      pickerLabel="Edit"
      pinUrl={props.pinUrl}
      searchUrl={props.searchUrl}
    />
  )
}

try{ DynamicSidePanelContent.displayName ||= 'DynamicSidePanelContent' } catch {}
try{ TopTeams.displayName ||= 'TopTeams' } catch {}
try{ TopRepos.displayName ||= 'TopRepos' } catch {}
try{ PinnedRepos.displayName ||= 'PinnedRepos' } catch {}